/**
 * SHO-10263: cart-enhancements pas bij cart-pagina, ATC / cart:update, of drawer-open.
 */

export const CART_IDLE_TIMEOUT_MS = 3000;
export const CART_IDLE_FALLBACK_MS = 1200;
export const CART_UPDATE_EVENT = 'cart:update';

/**
 * @param {string} src
 * @returns {string}
 */
export function scriptFilename(src) {
  const path = String(src).split('?')[0];
  const parts = path.split('/');
  return parts[parts.length - 1] || src;
}

/**
 * @param {ParentNode} root
 * @returns {{ modules: string[], classic: string[], immediate: boolean } | null}
 */
export function readCartEnhancementConfig(root = document) {
  const node = root.querySelector('[data-hds-cart-idle-bootstrap]');
  if (!node) return null;

  try {
    const parsed = JSON.parse(node.textContent || '{}');
    return {
      modules: Array.isArray(parsed.modules) ? parsed.modules.filter(Boolean) : [],
      classic: Array.isArray(parsed.classic) ? parsed.classic.filter(Boolean) : [],
      immediate: Boolean(parsed.immediate),
    };
  } catch {
    return null;
  }
}

/**
 * @param {Document} doc
 * @param {string} src
 * @returns {boolean}
 */
export function hasScriptWithSrc(doc, src) {
  const needle = scriptFilename(src);
  for (const script of doc.querySelectorAll('script[src]')) {
    const value = script.getAttribute('src') || '';
    if (value === src || scriptFilename(value) === needle) return true;
  }
  return false;
}

/**
 * @param {{ modules?: string[], classic?: string[] }} urls
 * @param {Document} [doc]
 * @returns {HTMLScriptElement[]}
 */
export function injectCartEnhancementScripts(urls, doc = document) {
  const created = [];
  const modules = urls.modules || [];
  const classic = urls.classic || [];

  for (const src of modules) {
    if (!src || hasScriptWithSrc(doc, src)) continue;
    const script = doc.createElement('script');
    script.type = 'module';
    script.src = src;
    script.dataset.hdsCartEnhancement = 'module';
    doc.head.appendChild(script);
    created.push(script);
  }

  for (const src of classic) {
    if (!src || hasScriptWithSrc(doc, src)) continue;
    const script = doc.createElement('script');
    script.src = src;
    script.defer = true;
    script.dataset.hdsCartEnhancement = 'classic';
    doc.body.appendChild(script);
    created.push(script);
  }

  return created;
}

/**
 * @param {HTMLScriptElement} script
 * @returns {Promise<void>}
 */
export function waitForScript(script) {
  if (script.dataset.hdsCartEnhancementLoaded === 'true') return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      script.dataset.hdsCartEnhancementLoaded = 'true';
      resolve();
    };

    script.addEventListener('load', done, { once: true });
    script.addEventListener('error', done, { once: true });

    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (/jsdom/i.test(ua)) {
      queueMicrotask(done);
    }
  });
}

/**
 * @param {object} [options]
 * @param {{ modules?: string[], classic?: string[], immediate?: boolean } | null} [options.config]
 * @param {Document} [options.doc]
 * @param {Window} [options.win]
 * @param {boolean} [options.listen]
 * @returns {() => HTMLScriptElement[]}
 */
export function installCartEnhancementBootstrap(options = {}) {
  const doc = options.doc || document;
  const win = options.win || window;
  const config = options.config === undefined ? readCartEnhancementConfig(doc) : options.config;
  const listen = options.listen !== false;

  let inflight = null;

  const start = () => {
    if (!config) return Promise.resolve([]);
    if (inflight) return inflight;

    const created = injectCartEnhancementScripts(config, doc);
    inflight = Promise.all(created.map((script) => waitForScript(script))).then(() => created);
    return inflight;
  };

  if (listen) {
    doc.addEventListener(CART_UPDATE_EVENT, start);
    win.addEventListener(CART_UPDATE_EVENT, start);
  }

  win.hdsEnsureCartEnhancements = start;

  if (!config) return start;

  if (config.immediate) start();

  return start;
}

if (typeof document !== 'undefined' && document.querySelector?.('[data-hds-cart-idle-bootstrap]')) {
  installCartEnhancementBootstrap();
}
